import React from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material';

function HomePhoneEditorDialog({ context, primitives }) {
    const {
        addDisplayChip,
        addHomePhoneTopBadge,
        deleteDraftOnlyProduct,
        editingHomePhone,
        findActiveHomePhoneProduct,
        isDraftOnlyProduct,
        isValidSortOrderInput,
        removeDisplayChip,
        removeHomePhoneTopBadge,
        saveHomePhoneDraft,
        savingDraft,
        setEditingHomePhone,
        updateDisplayChip,
        updateEditorValue,
        updateHomePhoneTopBadge,
        validateHomePhoneEditor
    } = context;
    const {
        renderChipListEditor,
        renderOfferLifecycleControls,
        renderProductDraftPreview
    } = primitives;

    const renderHomePhoneEditDialog = () => {
        if (!editingHomePhone) {
            return null;
        }

        const values = editingHomePhone.values;
        const localErrors = validateHomePhoneEditor(values);
        const sortOrderInvalid = !isValidSortOrderInput(values.sortOrder);
        const activeProduct = findActiveHomePhoneProduct(editingHomePhone.groupName, editingHomePhone.originalId);
        const canDelete = isDraftOnlyProduct({ id: editingHomePhone.originalId, ...values }, activeProduct);

        return (
            <Dialog open onClose={() => setEditingHomePhone(null)} fullWidth maxWidth="md">
                <DialogTitle>Home Phone Draft Edit</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <Alert severity="warning" variant="outlined">
                            Saving updates the draft config only. Publish Home Phone Draft is required before Bundle Builder sees these values.
                        </Alert>
                        <Alert severity="info" variant="outlined">
                            Most updates only need Offer Settings, Visibility, and Expiry Date. Customer email output changes live in the Customer Email Settings section.
                        </Alert>
                        <Typography variant="h6">Offer Settings / Bundle Builder Display</Typography>
                        <TextField
                            label="Product key"
                            value={editingHomePhone.originalId}
                            fullWidth
                            disabled
                            helperText="Product keys cannot change in this phase."
                        />
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={8}>
                                <TextField
                                    label="Display name"
                                    value={values.displayName}
                                    onChange={(event) => updateEditorValue('displayName', event.target.value)}
                                    fullWidth
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Sort order"
                                    type="number"
                                    value={values.sortOrder}
                                    onChange={(event) => updateEditorValue('sortOrder', event.target.value)}
                                    fullWidth
                                    error={sortOrderInvalid}
                                    helperText={sortOrderInvalid ? 'Sort order must be numeric.' : ''}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Promo/base price"
                                    type="number"
                                    value={values.price}
                                    onChange={(event) => updateEditorValue('price', event.target.value)}
                                    fullWidth
                                    inputProps={{ min: 0, step: '0.01' }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Regular/reference price"
                                    type="number"
                                    value={values.regularPrice}
                                    onChange={(event) => updateEditorValue('regularPrice', event.target.value)}
                                    fullWidth
                                    inputProps={{ min: 0, step: '0.01' }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                        <TextField
                            label="Required fees"
                                    type="number"
                                    value={values.requiredFees}
                                    onChange={(event) => updateEditorValue('requiredFees', event.target.value)}
                                    fullWidth
                                    inputProps={{ min: 0, step: '0.01' }}
                                />
                            </Grid>
                        </Grid>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            <FormControlLabel
                                control={<Switch checked={values.visible} onChange={(event) => updateEditorValue('visible', event.target.checked)} />}
                                label="Visible"
                            />
                            <FormControlLabel
                                control={<Switch checked={values.quoteEligible} onChange={(event) => updateEditorValue('quoteEligible', event.target.checked)} />}
                                label="Quote eligible"
                            />
                        </Stack>
                        {renderOfferLifecycleControls({
                            values,
                            updateValue: updateEditorValue,
                            canDelete,
                            onDelete: () => deleteDraftOnlyProduct({ scope: 'homePhone', productId: editingHomePhone.originalId, groupName: editingHomePhone.groupName }),
                            helperScope: 'Home Phone'
                        })}
                        <Divider />
                        <Box component="details">
                            <Box component="summary" sx={{ cursor: 'pointer', fontWeight: 700 }}>
                                Advanced Settings
                            </Box>
                            <Stack spacing={2} sx={{ mt: 2 }}>
                                <TextField
                                    label="Internal agent note"
                                    value={values.internalNotesText}
                                    onChange={(event) => updateEditorValue('internalNotesText', event.target.value)}
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    helperText="Use one note per line."
                                />
                            </Stack>
                        </Box>
                        <Divider />
                        <Box>
                            <Typography variant="h6" gutterBottom>Offer Display Details</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Draft display settings affect Bundle Builder only after Home Phone publish.
                            </Typography>
                        </Box>
                        <TextField
                            label="Card subtitle / description"
                            value={values.cardDescription}
                            onChange={(event) => updateEditorValue('cardDescription', event.target.value)}
                            fullWidth
                            multiline
                            minRows={2}
                            helperText="Example: 1 calling feature; Includes required 911 fees; original $35.89"
                        />
                        <TextField
                            label="Highlight note"
                            value={values.displayHighlightNote}
                            onChange={(event) => updateEditorValue('displayHighlightNote', event.target.value)}
                            fullWidth
                            helperText="Optional card callout shown to agents."
                        />
                        <TextField
                            label="Internal agent display note"
                            value={values.internalAgentNote}
                            onChange={(event) => updateEditorValue('internalAgentNote', event.target.value)}
                            fullWidth
                            multiline
                            minRows={2}
                        />
                        <TextField
                            label="Selected badge text"
                            value={values.selectedBadgeText}
                            onChange={(event) => updateEditorValue('selectedBadgeText', event.target.value)}
                            fullWidth
                        />
                        {renderChipListEditor({
                            title: 'Top Card Badges',
                            helperText: 'Top Card Badges appear at the top of the Bundle Builder card.',
                            chips: values.topBadges,
                            onUpdate: updateHomePhoneTopBadge,
                            onAdd: addHomePhoneTopBadge,
                            onRemove: removeHomePhoneTopBadge,
                            addLabel: 'Add Badge'
                        })}
                        {renderChipListEditor({
                            title: 'Detail Chips',
                            helperText: 'Detail Chips appear inside the card under the product description.',
                            chips: values.displayChips,
                            onUpdate: updateDisplayChip,
                            onAdd: addDisplayChip,
                            onRemove: removeDisplayChip,
                            addLabel: 'Add Chip'
                        })}
                        <Box>
                            {renderProductDraftPreview(values, {
                                categoryLabel: 'Home Phone',
                                priceValue: values.price
                            })}
                        </Box>
                        <Divider />
                        <Box>
                            <Typography variant="h6" gutterBottom>Customer Email Settings</Typography>
                            <Typography variant="body2" color="text.secondary">
                                These fields control customer-facing email output only.
                            </Typography>
                        </Box>
                        <FormControlLabel
                            control={<Switch checked={values.customerEmailEligible} onChange={(event) => updateEditorValue('customerEmailEligible', event.target.checked)} />}
                            label="Customer email eligible"
                        />
                        <TextField
                            label="Customer-facing email name"
                            value={values.emailDisplayName}
                            onChange={(event) => updateEditorValue('emailDisplayName', event.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="Customer-facing email bullets"
                            value={values.emailBulletsText}
                            onChange={(event) => updateEditorValue('emailBulletsText', event.target.value)}
                            fullWidth
                            multiline
                            minRows={3}
                            helperText="Use one bullet per line. Required when customer email eligible is on."
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditingHomePhone(null)} disabled={savingDraft}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={saveHomePhoneDraft} disabled={savingDraft || localErrors.length > 0 || sortOrderInvalid}>
                        {savingDraft ? 'Saving...' : 'Save Draft'}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };
    return renderHomePhoneEditDialog();
}

export default HomePhoneEditorDialog;
